import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import InputBase from '@mui/material/InputBase';
import { useAppSelector } from '../../app/hooks';
import { useSummaryQuery, useTransactionsQuery, useTransactionFiltersQuery } from '../../services/api';
import { api as apiClient } from '../../services/apiClient';
import { CHMAP, CHCOLOR, PARTY } from '../../constants/channels';
import { inr, inrShort, num, fmtPct, dfmt } from '../../utils/format';
import { KpiGrid } from '../../components/common/KpiCard';
import TableCard, { EmptyRow } from '../../components/common/TableCard';
import Pagination from '../../components/common/Pagination';
import AppButton from '../../components/common/AppButton';
import QueryState from '../../components/common/QueryState';
import { KpiSkeleton, TableRowsSkeleton } from '../../components/common/Shimmer';
import { SectionHeading } from '../../components/common/Bits';
import { FieldLabel, SelectField, CONTROL_SX } from '../../components/common/FormField';

const PER_PAGE = 12;

/** Mirrors the API's query string one-for-one: ?search=&page=1&test=&doctor=… */
const EMPTY_QUERY = { search: '', test: '', doctor: '', party: '', from: '', to: '', page: 1 };

const NUMERIC_SORTS = ['date', 'paid', 'mrp', 'discount', 'l2l', 'real'];

function SortableTh({ label, sortKey, sort, onSort, num: isNum }) {
  return (
    <th className={`sortable${isNum ? ' num' : ''}`} onClick={() => onSort(sortKey)}>
      {label} <span>{sort.key === sortKey ? (sort.dir === 'desc' ? '▼' : '▲') : ''}</span>
    </th>
  );
}

/** Waits for typing to settle so each keystroke doesn't become a request. */
function useDebounced(value, delay = 350) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return settled;
}

export default function LedgerTab({ channelId }) {
  const range = useAppSelector((s) => s.ui.range);
  const pm = PARTY[channelId];

  const [query, setQuery] = useState(EMPTY_QUERY);
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const debouncedSearch = useDebounced(query.search);

  // start clean when the channel or period changes underneath us
  useEffect(() => {
    setQuery(EMPTY_QUERY);
  }, [channelId, range]);

  const params = useMemo(
    () => ({
      channel: channelId,
      range,
      search: debouncedSearch,
      test: query.test,
      doctor: query.doctor,
      party: query.party,
      from: query.from,
      to: query.to,
      page: query.page,
      limit: PER_PAGE,
      sort: sort.key,
      dir: sort.dir,
    }),
    [channelId, range, debouncedSearch, query.test, query.doctor, query.party, query.from, query.to, query.page, sort]
  );

  const summaryQuery = useSummaryQuery({ range, channel: channelId });
  const rowsQuery = useTransactionsQuery(params);
  const filtersQuery = useTransactionFiltersQuery({ channel: channelId, range });

  const setParam = (key, value) => setQuery((q) => ({ ...q, [key]: value, page: 1 }));

  const onSort = (k) => {
    setSort((s) =>
      s.key === k
        ? { key: k, dir: s.dir === 'desc' ? 'asc' : 'desc' }
        : { key: k, dir: NUMERIC_SORTS.includes(k) ? 'desc' : 'asc' }
    );
    // Re-ordering makes page 4 a different twelve rows. Someone sorting by "Paid
    // Amount" wants the top of that order, not wherever they happened to be paged to.
    setQuery((q) => (q.page === 1 ? q : { ...q, page: 1 }));
  };

  const activeChips = [
    query.search && `“${query.search}”`,
    query.test, query.doctor, query.party,
    query.from && `from ${dfmt(query.from)}`,
    query.to && `to ${dfmt(query.to)}`,
  ].filter(Boolean);

  const exportCsv = async () => {
    setExporting(true);
    setExportError('');
    try {
      // the export is the whole filtered set, so paging and sorting don't apply
      const { page, limit, sort: _sort, dir: _dir, ...filterOnly } = params;
      await apiClient.download('/transactions/export', filterOnly, `ledger_${channelId}.csv`);
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const filterSx = { ...CONTROL_SX, p: '8px 10px', borderRadius: '9px', background: 'var(--card)' };
  const opts = filtersQuery.data || { tests: [], doctors: [], parties: [], minDate: '', maxDate: '' };
  const rows = rowsQuery.data;

  return (
    <>
      <QueryState query={summaryQuery} skeleton={<KpiSkeleton columns={4} />}>
        {(s) => (
          <KpiGrid
            columns={4}
            items={[
              { label: 'Revenue', value: inrShort(s.current.rev), sub: `${s.current.n} txns`, accent: CHCOLOR[channelId] },
              { label: 'Avg Ticket', value: inr(s.current.avg), sub: 'Paid per txn', accent: 'var(--s3)' },
              { label: 'Discount', value: inrShort(s.current.disc), sub: `${fmtPct(s.current.discPct)} of MRP`, accent: 'var(--s2)' },
              { label: 'Realization', value: fmtPct(s.current.real), sub: `of ${inrShort(s.current.mrp)} MRP`, accent: 'var(--s4)' },
            ]}
          />
        )}
      </QueryState>

      <SectionHeading>Transaction ledger · {CHMAP[channelId].name}</SectionHeading>

      <TableCard
        title={rows ? `${num(rows.total)} transactions` : 'Transactions'}
        actions={
          <AppButton onClick={exportCsv} disabled={exporting || !rows?.total}>
            {exporting ? 'Preparing…' : '⤓ Export CSV (filtered)'}
          </AppButton>
        }
        header={
          <>
            {/* filter bar — every control sits on the 12-column grid */}
            <Grid
              container
              spacing={1.5}
              sx={{
                alignItems: 'flex-end',
                p: '15px 18px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--card-2)',
                m: 0,
                width: '100%',
              }}
            >
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                <FieldLabel>Patient / Barcode</FieldLabel>
                <InputBase
                  fullWidth
                  sx={filterSx}
                  placeholder="Name or barcode…"
                  value={query.search}
                  onChange={(e) => setParam('search', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                <SelectField
                  label="Test name"
                  inputSx={filterSx}
                  value={query.test}
                  onChange={(e) => setParam('test', e.target.value)}
                  options={[{ value: '', label: 'All' }, ...opts.tests]}
                  renderValue={(v) => v || 'All'}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                <SelectField
                  label="Doctor name"
                  inputSx={filterSx}
                  value={query.doctor}
                  onChange={(e) => setParam('doctor', e.target.value)}
                  options={[{ value: '', label: 'All' }, ...opts.doctors]}
                  renderValue={(v) => v || 'All'}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                <SelectField
                  label={pm.label}
                  inputSx={filterSx}
                  value={query.party}
                  onChange={(e) => setParam('party', e.target.value)}
                  options={[{ value: '', label: 'All' }, ...opts.parties]}
                  renderValue={(v) => v || 'All'}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2, lg: 1.25 }}>
                <FieldLabel>Date from</FieldLabel>
                <InputBase
                  fullWidth type="date" sx={filterSx}
                  inputProps={{ min: opts.minDate || undefined, max: opts.maxDate || undefined }}
                  value={query.from} onChange={(e) => setParam('from', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2, lg: 1.25 }}>
                <FieldLabel>Date to</FieldLabel>
                <InputBase
                  fullWidth type="date" sx={filterSx}
                  inputProps={{ min: opts.minDate || undefined, max: opts.maxDate || undefined }}
                  value={query.to} onChange={(e) => setParam('to', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 1.5 }}>
                <AppButton fullWidth sx={{ whiteSpace: 'nowrap' }} onClick={() => setQuery(EMPTY_QUERY)}>
                  ✕ Clear filters
                </AppButton>
              </Grid>
            </Grid>

            <Box sx={{ p: '9px 18px', fontSize: 12, color: 'var(--ink-2)', borderBottom: '1px solid var(--grid)', background: 'var(--card)' }}>
              {!rows ? (
                'Loading…'
              ) : activeChips.length ? (
                <>Filtered: <b>{num(rows.total)}</b> txns · <b>{inr(rows.revenue)}</b> collected — {activeChips.join(' · ')}</>
              ) : (
                <>Showing all <b>{num(rows.total)}</b> transactions · <b>{inr(rows.revenue)}</b> collected</>
              )}
            </Box>
          </>
        }
        footer={
          rows && (
            <>
              <span>
                Showing {rows.items.length ? (rows.page - 1) * rows.limit + 1 : 0}–
                {(rows.page - 1) * rows.limit + rows.items.length} of {num(rows.total)}
              </span>
              <Pagination
                page={rows.page}
                pages={rows.pages}
                onChange={(p) => setQuery((q) => ({ ...q, page: p }))}
              />
            </>
          )
        }
      >
        <QueryState query={rowsQuery} skeleton={<TableRowsSkeleton columns={11} rows={12} />}>
          {(data) => (
            // dim rather than blank while the next page is on its way
            <Box sx={{ opacity: rowsQuery.isFetching ? 0.55 : 1, transition: 'opacity .15s' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <SortableTh label="Date" sortKey="date" sort={sort} onSort={onSort} />
                    <SortableTh label="Barcode" sortKey="barcode" sort={sort} onSort={onSort} />
                    <SortableTh label="Patient" sortKey="patient" sort={sort} onSort={onSort} />
                    <SortableTh label={pm.label} sortKey="party" sort={sort} onSort={onSort} />
                    <SortableTh label="Doctor" sortKey="doctor" sort={sort} onSort={onSort} />
                    <SortableTh label="Test" sortKey="test" sort={sort} onSort={onSort} />
                    <SortableTh label="MRP" sortKey="mrp" sort={sort} onSort={onSort} num />
                    <SortableTh label="L2L Price" sortKey="l2l" sort={sort} onSort={onSort} num />
                    <SortableTh label="Discount" sortKey="discount" sort={sort} onSort={onSort} num />
                    <SortableTh label="Paid Amount" sortKey="paid" sort={sort} onSort={onSort} num />
                    <SortableTh label="Real." sortKey="real" sort={sort} onSort={onSort} num />
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 ? (
                    <EmptyRow colSpan={11}>No matching transactions</EmptyRow>
                  ) : (
                    data.items.map((x) => (
                      <tr key={x.id}>
                        <td>{dfmt(x.date)}</td>
                        <td className="code">{x.barcode}</td>
                        <td>{x.patient}</td>
                        <td>{x.party}</td>
                        <td>{x.doctor}</td>
                        <td>{x.test}</td>
                        <td className="num">{inr(x.mrp)}</td>
                        <td className="num">{inr(x.l2l)}</td>
                        <td className="num disc">{x.discount ? '−' + inr(x.discount) : '₹0'}</td>
                        <td className="num"><b>{inr(x.paid)}</b></td>
                        <td className="num">{fmtPct(x.real, 0)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Box>
          )}
        </QueryState>
      </TableCard>

      {exportError && (
        <Box sx={{ mt: 1.5, fontSize: 12.5, color: 'var(--crit)' }}>Export failed — {exportError}</Box>
      )}
    </>
  );
}
